import React, { useEffect, useState } from 'react';
import {
  UserIcon,
  MapPinIcon,
  IdentificationIcon,
  InformationCircleIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import {
  TextField,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import type { StepIconProps } from '@mui/material/StepIcon';

import { useAppDispatch, useAppSelector } from '../../../../../store';
import { createLead, updateLead } from '../../../store/slices/leadSlice';
import { createContact, updateContact } from '../../../store/slices/contactSlice';
import {
  fetchLeadSourceOptions,
  selectLeadSourceLoaded,
  selectLeadSourceOptions,
} from '../../../store/slices/leadSourceSlice';
import type { Lead, Contact } from '../../../types';
import { DEFAULT_LEAD_SOURCE, FALLBACK_LEAD_SOURCE_OPTIONS, toDropdownOptions } from '../../../data/leadSourceOptions';

import TextInputField from '../../form/TextInputField';
import EmailField from '../../form/EmailField';
import PhoneField from '../../form/PhoneField';
import CityField from '../../form/CityField';
import StateField from '../../form/StateField';
import ZipCodeField from '../../form/ZipCodeField';
import DetailRow from '../../form/DetailRow';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import { useLeadStages } from '../../../hooks/useLeadStages';
import DropdownField from '../../form/DropdownField';

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage:
        'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1,
    ...theme.applyStyles?.('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme }) => ({
  backgroundColor: '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...theme.applyStyles?.('dark', {
    backgroundColor: theme.palette.grey[700],
  }),
  variants: [
    {
      props: ({ ownerState }: { ownerState: { active?: boolean } }) => ownerState.active,
      style: {
        backgroundImage:
          'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
        boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
      },
    },
    {
      props: ({ ownerState }: { ownerState: { completed?: boolean } }) =>
        ownerState.completed,
      style: {
        backgroundImage:
          'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)',
      },
    },
  ],
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement<unknown> } = {
    1: <InformationCircleIcon className="h-6 w-6" />,
    2: <BuildingOfficeIcon className="h-6 w-6" />,
    3: <CheckCircleIcon className="h-6 w-6" />,
  };

  const tooltips: { [index: string]: string } = {
    1: 'Information',
    2: 'Site',
    3: 'Review & Save',
  };

  const iconElement = icons[String(props.icon)];

  return (
    <Tooltip title={tooltips[String(props.icon)] || ''} arrow>
      <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
        {iconElement}
      </ColorlibStepIconRoot>
    </Tooltip>
  );
}

interface AddLeadModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  initialData?: Contact | Lead;
  mode?: 'lead' | 'contact';
}

interface LeadFormData {
  // Contact Information (Step 1)
  fullName: string;
  phone: string;
  whatsapp: string;
  referenceBy: string;
  physicalAddress: string;
  city: string | number | null;
  state: string | number | null;
  type: 'business' | 'individual';
  businessName: string;
  gstNumber: string;
  panNumber: string;
  // Site Information (Step 2)
  numberOfUnits: string;
  siteName: string;
  siteAddress: string;
  siteCity: string | number | null;
  siteState: string | number | null;
  siteZip: string;
  contactPersonName: string;
  contactPersonMobile: string;
  // Additional fields (Step 1 / meta)
  email: string;
  leadSource: string;
  budgetRange: string;
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({
  isOpen,
  onRequestClose,
  initialData,
  mode = 'lead',
}) => {
  const dispatch = useAppDispatch();
  const { contacts } = useAppSelector((state) => state.contacts);
  const { leads } = useAppSelector((state) => state.leads);
  const leadSourceOptionsFromStore = useAppSelector(selectLeadSourceOptions);
  const leadSourceLoaded = useAppSelector(selectLeadSourceLoaded);
  const { getStageOrder } = useLeadStages();

  const [activeStep, setActiveStep] = useState(0);
  const [sameAsAboveWhatsapp, setSameAsAboveWhatsapp] = useState(false);
  const [hasGstNumber, setHasGstNumber] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [existingContact, setExistingContact] = useState<Contact | Lead | null>(null);
  const [mobileInputValue, setMobileInputValue] = useState('');
  const [isViewMode, setIsViewMode] = useState(false);
  const defaultLeadSource = DEFAULT_LEAD_SOURCE;

  // Create options from contacts and leads for autocomplete
  const mobileOptions = React.useMemo(() => {
    const options: Array<{ id: string; name: string; phone: string; email: string; data: Contact | Lead; type: 'contact' | 'lead' }> = [];
    console.log("existingContact", existingContact);

    // Add contacts
    contacts.forEach((contact) => {
      options.push({
        id: contact.id,
        name: contact.name || 'Unknown', // Fallback for name
        phone: contact.phone || '', // Fallback for phone
        email: contact.email || '', // Fallback for email
        data: contact,
        type: 'contact',
      });
    });

    // Add leads
    leads.forEach((lead) => {
      options.push({
        id: lead.id,
        name: lead.name || 'Unknown',
        phone: (lead.phone || '').replace(/[^0-9]/g, '').slice(-10), // Extract last 10 digits
        email: lead.email || '',
        data: lead,
        type: 'lead',
      });
    });

    return options;
  }, [contacts, leads]);
  const [formData, setFormData] = useState<LeadFormData>({
    numberOfUnits: '',
    fullName: '',
    phone: '',
    whatsapp: '',
    referenceBy: '',
    physicalAddress: '',
    city: null,
    state: null,
    type: 'individual',
    businessName: '',
    gstNumber: '',
    panNumber: '',
    siteName: '',
    siteAddress: '',
    siteCity: null,
    siteState: null,
    siteZip: '',
    contactPersonName: '',
    contactPersonMobile: '',
    email: '',
    leadSource: defaultLeadSource,
    budgetRange: '',
  });
  const leadSourceOptions = React.useMemo(() => {
    const apiSources = leads
      .map((lead) => (lead.source || '').trim())
      .filter(Boolean);
    const selectedSource = (formData.leadSource || '').trim();
    const merged = new Set([...FALLBACK_LEAD_SOURCE_OPTIONS, ...leadSourceOptionsFromStore, ...apiSources]);

    if (selectedSource) {
      merged.add(selectedSource);
    }

    return toDropdownOptions(Array.from(merged));
  }, [leads, leadSourceOptionsFromStore, formData.leadSource]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touchedWhileTyping, setTouchedWhileTyping] = useState({
    email: false,
    phone: false,
    whatsapp: false,
    siteZip: false,
    contactPersonMobile: false,
  });

  // Type guard to distinguish Contact vs Lead at runtime
  const isContactEntity = (entity: Contact | Lead | null | undefined): entity is Contact => {
    if (!entity) return false;
    const status = (entity as any).status ? String((entity as any).status).toLowerCase() : '';
    // Leads have specific statuses
    const leadStatuses = ['new', 'contacted', 'qualified', 'converted', 'lost'];
    if (leadStatuses.includes(status)) return false;
    // Leads have detailed stages
    if ('detailedStage' in entity || 'contactId' in entity) return false;
    // Contacts have these statuses
    const contactStatuses = ['active', 'inactive', 'pending'];
    if (contactStatuses.includes(status)) return true;
    // Fallback for contact edit mode: if it has an ID and we're in contact mode, it's likely our contact
    return mode === 'contact' && !!(entity as any).id;
  };

  // Populate form with existing contact/lead data
  const isLeadEntity = (entity: Contact | Lead | null | undefined): entity is Lead => {
    if (!entity) return false;
    return !isContactEntity(entity) && ('detailedStage' in entity || 'contactId' in entity || !!(entity as Lead).id);
  };

  const populateFormFromExisting = (data: Contact | Lead) => {
    const isLead = !isContactEntity(data);
    const leadData = isLead ? data as Lead : undefined;
    const contactData = isLead ? (data as Lead).contact : data as Contact;

    const extractedPhone = (contactData?.phone || leadData?.phone || '').replace(/[^0-9]/g, '').slice(-10);
    const extractedWhatsapp = (contactData?.whatsapp || leadData?.whatsapp || '').replace(/[^0-9]/g, '').slice(-10);

    // Use values from contactData if available, otherwise fallback to leadData's flattened fields
    setFormData({
      numberOfUnits: leadData?.numberOfUnits?.toString() || '',
      fullName: contactData?.name || leadData?.name || '',
      phone: extractedPhone,
      whatsapp: extractedWhatsapp,
      referenceBy: leadData?.referenceBy || contactData?.referenceBy || '',
      physicalAddress: leadData?.physicalAddress || '',
      city: leadData?.city || null,
      state: leadData?.state || null,
      type: leadData?.type || 'individual',
      businessName: leadData?.company || '',
      gstNumber: leadData?.gstNumber || '',
      panNumber: leadData?.panNumber || '',
      siteName: leadData?.siteName || leadData?.company || '',
      siteAddress: leadData?.siteAddress || '',
      siteCity: leadData?.siteCity || null,
      siteState: leadData?.siteState || null,
      siteZip: leadData?.siteZip || '',
      contactPersonName: leadData?.contactPersonName || contactData?.name || leadData?.name || '',
      contactPersonMobile: ((leadData?.contactPersonMobile || contactData?.phone || leadData?.phone || '').replace(/[^0-9]/g, '').slice(-10)),
      email: (contactData as any)?.email || leadData?.email || '',
      leadSource: leadData?.source || defaultLeadSource,
      budgetRange: leadData?.value ? `₹${(leadData.value || 0).toLocaleString()}` : '',
    });
    setSameAsAboveWhatsapp(extractedPhone === extractedWhatsapp && extractedWhatsapp.length > 0);
    setHasGstNumber(!!leadData?.gstNumber);
    setExistingContact(data);
    setMobileInputValue(`${contactData?.name || leadData?.name} - ${(contactData?.phone || leadData?.phone || '').replace(/[^0-9]/g, '').slice(-10)}`);

    // When editing existing data (Lead or Contact), start at Step 0 in edit mode
    // EXCEPT if we are creating a NEW lead for an existing contact, then start at step 1
    setIsViewMode(false);
    if (isContactEntity(data) && mode === 'lead') {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  };

  // Handle mobile autocomplete selection
  const handleMobileChange = (_event: any, newValue: any) => {
    if (newValue && typeof newValue === 'object' && 'data' in newValue) {
      // Selected an existing contact/lead from dropdown
      populateFormFromExisting(newValue.data);
      setMobileInputValue(`${newValue.name} - ${newValue.phone}`);
    } else if (typeof newValue === 'string') {
      // User typed a new number (freeSolo mode)
      const digitsOnly = newValue.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        phone: digitsOnly,
      }));
      setExistingContact(null);
      setIsViewMode(false);
      // Keep the input as user typed it if it's not just digits
      if (newValue.includes('-') || newValue.includes(' ')) {
        setMobileInputValue(newValue);
      } else {
        setMobileInputValue(digitsOnly);
      }
    } else {
      // Cleared selection
      setFormData((prev) => ({
        ...prev,
        phone: '',
      }));
      setExistingContact(null);
      setIsViewMode(false);
      setMobileInputValue('');
    }
  };

  // Handle mobile input change (for free text input)
  const handleMobileInputChange = (_event: any, newInputValue: string) => {
    setMobileInputValue(newInputValue);
    // Extract digits from input (only allow digits) - MAX 10 digits
    const digitsOnly = newInputValue.replace(/[^0-9]/g, '').slice(0, 10);

    // If first digit is entered, validate it's a valid Indian mobile prefix (6, 7, 8, or 9)
    // If invalid first digit, don't allow it
    let validDigits = digitsOnly;
    if (digitsOnly.length > 0) {
      const firstDigit = digitsOnly[0];
      if (!/^[6-9]$/.test(firstDigit)) {
        // Invalid first digit - don't accept it, keep previous value
        return;
      }
    }

    // Clear existing contact immediately when user starts typing to allow free editing
    // This ensures the Autocomplete doesn't lock the input
    if (existingContact) {
      // Extract phone from current input value to compare
      const currentPhoneFromInput = newInputValue.includes('-')
        ? newInputValue.split(' - ')[1]?.replace(/[^0-9]/g, '') || ''
        : newInputValue.replace(/[^0-9]/g, '');

      // If user is typing something different, clear existing contact
      if (currentPhoneFromInput !== formData.phone || validDigits !== formData.phone) {
        setExistingContact(null);
      }
    }

    // Update phone field with digits only (max 10 digits)
    setFormData((prev) => ({
      ...prev,
      phone: validDigits,
    }));

    // Validate and clear error if user is typing a valid number
    if (validDigits.length === 10 && /^[6-9]\d{9}$/.test(validDigits)) {
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
      const matchingOption = mobileOptions.find(
        (opt) => opt.phone === validDigits
      );
      if (!matchingOption) {
        // New number, clear existing contact
        setExistingContact(null);
      }
    } else if (validDigits.length > 0 && validDigits.length < 10) {
      // Not enough digits yet, clear existing
      setExistingContact(null);
      // Show error if user has entered digits but not complete
      if (validDigits.length > 0 && !/^[6-9]/.test(validDigits)) {
        setErrors((prev) => ({
          ...prev,
          phone: 'Invalid Indian mobile number. Must start with 6, 7, 8, or 9'
        }));
      } else if (errors.phone && validDigits.length < 10) {
        // Clear length error if user is still typing
        if (errors.phone.includes('exactly 10 digits')) {
          setErrors((prev) => ({ ...prev, phone: '' }));
        }
      }
    }
  };

  // Clear existing contact and allow creating new
  // const handleClearExisting = () => {
  //   setExistingContact(null);
  //   setMobileInputValue(formData.phone); // Keep the phone number in input
  //   setFormData({
  //     numberOfUnits: '',
  //     fullName: '',
  //     phone: formData.phone, // Keep the phone number
  //     whatsapp: '',
  //     referenceBy: '',
  //     physicalAddress: '',
  //     city: null,
  //     state: null,
  //     type: 'individual',
  //     businessName: '',
  //     gstNumber: '',
  //     panNumber: '',
  //     siteName: '',
  //     siteAddress: '',
  //     siteCity: null,
  //     siteState: null,
  //     siteZip: '',
  //     contactPersonName: '',
  //     contactPersonMobile: '',
  //     email: '',
  //     leadSource: 'Direct',
  //     budgetRange: '',
  //   });
  //   setSameAsAboveWhatsapp(false);
  //   setHasGstNumber(false);
  // };

  useEffect(() => {
    if (isOpen && !leadSourceLoaded) {
      dispatch(fetchLeadSourceOptions());
    }
  }, [isOpen, leadSourceLoaded, dispatch]);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setActiveStep(0);
      setFormData({
        numberOfUnits: '',
        fullName: '',
        phone: '',
        whatsapp: '',
        referenceBy: '',
        physicalAddress: '',
        city: null,
        state: null,
        type: 'individual',
        businessName: '',
        gstNumber: '',
        panNumber: '',
        siteName: '',
        siteAddress: '',
        siteCity: null,
        siteState: null,
        siteZip: '',
        contactPersonName: '',
        contactPersonMobile: '',
        email: '',
        leadSource: defaultLeadSource,
        budgetRange: '',
      });
      setErrors({});
      setSameAsAboveWhatsapp(false);
      setHasGstNumber(false);
      setExistingContact(null);
      setIsViewMode(false);
      setMobileInputValue('');
      setTouchedWhileTyping({
        email: false,
        phone: false,
        whatsapp: false,
        siteZip: false,
        contactPersonMobile: false,
      });
    } else if (isOpen && initialData) {
      // Populate form with initial data when modal opens
      populateFormFromExisting(initialData);
    }
  }, [isOpen, initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Don't allow changes in view mode
    if (isViewMode) {
      return;
    }

    const { name, value } = e.target;

    // Skip phone field as it's handled by autocomplete
    if (name === 'phone') {
      return;
    }

    // Enforce character limits (including spaces and all characters)
    let processedValue = value;
    const fieldsWith20CharLimit = ['fullName', 'referenceBy', 'siteName', 'contactPersonName'];
    const fieldsWith50CharLimit = ['physicalAddress', 'siteAddress'];

    if (fieldsWith20CharLimit.includes(name)) {
      processedValue = value.slice(0, 20);
    } else if (fieldsWith50CharLimit.includes(name)) {
      processedValue = value.slice(0, 50);
    }

    if (!touchedWhileTyping[name as keyof typeof touchedWhileTyping]) {
      setTouchedWhileTyping((prev) => ({
        ...prev,
        [name]: true,
      }));
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
      ...(name === 'leadSource' && !['Reference', 'Direct'].includes(processedValue)
        ? { referenceBy: '' }
        : {}),
    }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (name: string) => {
    if (!touchedWhileTyping[name as keyof typeof touchedWhileTyping]) {
      setTouchedWhileTyping((prev) => ({
        ...prev,
        [name]: true,
      }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Only allow digits
    const numericValue = value.replace(/[^0-9]/g, '');

    setFormData((prev) => ({
      ...prev,
      [name]: numericValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAlphanumericChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Only allow alphanumeric characters (0-9 and A-Z), convert to uppercase
    let alphanumericValue = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();

    // Enforce fixed size limits
    if (name === 'gstNumber') {
      alphanumericValue = alphanumericValue.slice(0, 15); // GST number is exactly 15 characters
    } else if (name === 'panNumber') {
      alphanumericValue = alphanumericValue.slice(0, 10); // PAN number is exactly 10 characters
    }

    setFormData((prev) => ({
      ...prev,
      [name]: alphanumericValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      // Step 1: Information validation
      if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Mobile number is required';
        setTouchedWhileTyping((prev) => ({ ...prev, phone: true }));
      } else if (formData.phone.length !== 10) {
        newErrors.phone = 'Indian mobile number must be exactly 10 digits';
        setTouchedWhileTyping((prev) => ({ ...prev, phone: true }));
      } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
        newErrors.phone = 'Invalid mobile number. Must start with 6, 7, 8, or 9';
        setTouchedWhileTyping((prev) => ({ ...prev, phone: true }));
      }
      if (formData.type === 'business' && !formData.businessName.trim()) {
        newErrors.businessName = 'Business name is required';
      }
      if (formData.leadSource === 'Reference' && !formData.referenceBy.trim()) {
        newErrors.referenceBy = 'Refered By is required for Reference source';
      }
      // City and State validation for contact information
      if (!formData.city || formData.city === null || formData.city === '') {
        newErrors.city = 'City is required';
      }
      if (!formData.state || formData.state === null || formData.state === '') {
        newErrors.state = 'State is required';
      }
      // GST and PAN number validation
      if (formData.type === 'business') {
        if (hasGstNumber) {
          if (!formData.gstNumber.trim()) {
            newErrors.gstNumber = 'GST number is required';
          } else if (formData.gstNumber.length !== 15) {
            newErrors.gstNumber = 'GST number must be exactly 15 characters';
          }
        } else {
          if (!formData.panNumber.trim()) {
            newErrors.panNumber = 'PAN number is required';
          } else if (formData.panNumber.length !== 10) {
            newErrors.panNumber = 'PAN number must be exactly 10 characters';
          }
        }
      }
    } else if (step === 1) {
      // Step 2: Site validation
      if (!formData.numberOfUnits.trim()) {
        newErrors.numberOfUnits = 'Number of units is required';
      }
      if (!formData.siteName.trim()) {
        newErrors.siteName = 'Site name is required';
      }
      if (!formData.siteAddress.trim()) {
        newErrors.siteAddress = 'Site address is required';
      }
      // Site City and State validation
      if (!formData.siteCity || formData.siteCity === null || formData.siteCity === '') {
        newErrors.siteCity = 'Site city is required';
      }
      if (!formData.siteState || formData.siteState === null || formData.siteState === '') {
        newErrors.siteState = 'Site state is required';
      }
      // if (!formData.siteZip.trim()) {
      //   newErrors.siteZip = 'Zip code is required';
      //   setTouchedWhileTyping((prev) => ({ ...prev, siteZip: true }));
      // }
      if (!formData.contactPersonName.trim()) {
        newErrors.contactPersonName = 'Contact person name is required';
      }
      if (!formData.contactPersonMobile.trim()) {
        newErrors.contactPersonMobile = 'Contact person mobile is required';
        setTouchedWhileTyping((prev) => ({ ...prev, contactPersonMobile: true }));
      } else if (formData.contactPersonMobile.length !== 10) {
        newErrors.contactPersonMobile = 'Mobile number must be exactly 10 digits';
        setTouchedWhileTyping((prev) => ({ ...prev, contactPersonMobile: true }));
      } else if (!/^[6-9]\d{9}$/.test(formData.contactPersonMobile)) {
        newErrors.contactPersonMobile = 'Invalid mobile number.';
        setTouchedWhileTyping((prev) => ({ ...prev, contactPersonMobile: true }));
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Step 0: Contact Information validations
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Mobile number is required';
    } else if (formData.phone.length !== 10) {
      newErrors.phone = 'Mobile number must be exactly 10 digits';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid mobile number';
    }
    // City and State validation for contact information
    if (!formData.city || formData.city === null || formData.city === '') {
      newErrors.city = 'City is required';
    }
    if (!formData.state || formData.state === null || formData.state === '') {
      newErrors.state = 'State is required';
    }
    if (formData.type === 'business' && !formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    if (formData.leadSource === 'Reference' && !formData.referenceBy.trim()) {
      newErrors.referenceBy = 'Refered By is required for Reference source';
    }
    // GST and PAN number validation
    if (formData.type === 'business') {
      if (hasGstNumber) {
        if (!formData.gstNumber.trim()) {
          newErrors.gstNumber = 'GST number is required';
        } else if (formData.gstNumber.length !== 15) {
          newErrors.gstNumber = 'GST number must be exactly 15 characters';
        }
      } else {
        if (!formData.panNumber.trim()) {
          newErrors.panNumber = 'PAN number is required';
        } else if (formData.panNumber.length !== 10) {
          newErrors.panNumber = 'PAN number must be exactly 10 characters';
        }
      }
    }

    // Step 1: Site Information validations
    if (!formData.numberOfUnits.trim()) {
      newErrors.numberOfUnits = 'Number of units is required';
    }
    if (!formData.siteName.trim()) {
      newErrors.siteName = 'Site name is required';
    }
    if (!formData.siteAddress.trim()) {
      newErrors.siteAddress = 'Site address is required';
    }
    // Site City and State validation
    if (!formData.siteCity || formData.siteCity === null || formData.siteCity === '') {
      newErrors.siteCity = 'Site city is required';
    }
    if (!formData.siteState || formData.siteState === null || formData.siteState === '') {
      newErrors.siteState = 'Site state is required';
    }
    // if (!formData.siteZip.trim()) {
    //   newErrors.siteZip = 'Zip code is required';
    // }
    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }
    if (!formData.contactPersonMobile.trim()) {
      newErrors.contactPersonMobile = 'Contact person mobile is required';
    } else if (formData.contactPersonMobile.length !== 10) {
      newErrors.contactPersonMobile = 'Mobile number must be exactly 10 digits';
    } else if (!/^[6-9]\d{9}$/.test(formData.contactPersonMobile)) {
      newErrors.contactPersonMobile = 'Invalid mobile number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid for Confirm button (without setting errors)
  const isFormValid = (): boolean => {
    // Check Step 0: Contact Information
    if (!formData.fullName.trim()) return false;
    if (!formData.phone.trim() || formData.phone.length !== 10 || !/^[6-9]\d{9}$/.test(formData.phone)) return false;
    if (!formData.city || formData.city === null || formData.city === '') return false;
    if (!formData.state || formData.state === null || formData.state === '') return false;
    if (formData.type === 'business' && !formData.businessName.trim()) return false;
    if (formData.leadSource === 'Reference' && !formData.referenceBy.trim()) return false;
    if (formData.type === 'business') {
      if (hasGstNumber) {
        if (!formData.gstNumber.trim() || formData.gstNumber.length !== 15) return false;
      } else {
        if (!formData.panNumber.trim() || formData.panNumber.length !== 10) return false;
      }
    }

    // Check Step 1: Site Information
    if (!formData.numberOfUnits.trim()) return false;
    if (!formData.siteName.trim()) return false;
    if (!formData.siteAddress.trim()) return false;
    if (!formData.siteCity || formData.siteCity === null || formData.siteCity === '') return false;
    if (!formData.siteState || formData.siteState === null || formData.siteState === '') return false;
    // if (!formData.siteZip.trim()) return false;
    if (!formData.contactPersonName.trim()) return false;
    if (!formData.contactPersonMobile.trim() || formData.contactPersonMobile.length !== 10 || !/^[6-9]\d{9}$/.test(formData.contactPersonMobile)) return false;

    return true;
  };

  const handleNext = () => {
    if (!isViewMode && validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (!isViewMode) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleStateChange = (stateName: string) => {
    setFormData((prev) => ({
      ...prev,
      state: stateName,
    }));
  };

  const handleSiteStateChange = (stateName: string) => {
    setFormData((prev) => ({
      ...prev,
      siteState: stateName,
    }));
  };

  const handleStepClick = (step: number) => {
    if (!isViewMode && step >= 0 && step <= 2) {
      setActiveStep(step);
    }
  };

  // Handle Edit button click - enable editing mode
  const handleEditClick = () => {
    setIsViewMode(false);
    setActiveStep(0);
  };

  useEffect(() => {
    if (sameAsAboveWhatsapp) {
      setFormData((prev) => ({
        ...prev,
        whatsapp: prev.phone || '',
      }));
    }
  }, [sameAsAboveWhatsapp, formData.phone]);

  const handleCheckboxChangeWhatsapp = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSameAsAboveWhatsapp(e.target.checked);
  };

  const handleGstCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasGstNumber(e.target.checked);
    // Clear the opposite field when switching
    if (e.target.checked) {
      setFormData((prev) => ({
        ...prev,
        panNumber: '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        gstNumber: '',
      }));
    }
  };

  const hasFormData = (): boolean => {
    return !!(
      formData.fullName.trim() ||
      formData.email.trim() ||
      formData.phone.trim() ||
      formData.physicalAddress.trim() ||
      formData.budgetRange.trim()
    );
  };

  const handleClose = () => {
    if (hasFormData()) {
      setIsConfirmDialogOpen(true);
    } else {
      onRequestClose();
    }
  };

  const handleConfirmClose = () => {
    setIsConfirmDialogOpen(false);
    onRequestClose();
  };

  const handleCancelClose = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (mode === 'contact') {
      const trimmedReferenceBy = formData.referenceBy.trim();
      const baseContact: Partial<Contact> = {
        name: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        whatsapp: formData.whatsapp ? formData.whatsapp.trim() : undefined,
        status: isContactEntity(initialData) ? initialData.status : 'active',
        referenceBy: trimmedReferenceBy || undefined,
      };

      if (isContactEntity(initialData)) {
        const updatedContact: Contact = {
          ...initialData,
          ...baseContact,
          // Explicitly clear on edit when user empties the field.
          referenceBy: trimmedReferenceBy || (null as any),
        } as Contact;
        dispatch(updateContact({ id: initialData.id, data: updatedContact }));
      } else {
        try {
          const actionResult = await dispatch(createContact(baseContact));
          const newContact = actionResult.payload as Contact;

          if (newContact && newContact.id) {
            // Also create a lead associated with this contact
            const newLead: Partial<Lead> = {
              company: formData.type === 'business' ? formData.businessName.trim() : formData.fullName.trim(),
              source: formData.leadSource || defaultLeadSource,
              status: 'new',
              stageId: getStageOrder('new'),
              detailedStage: 'new',
              currentStage: 'new',
              stage: 'new',
              contactId: newContact.id,
              siteName: formData.siteName.trim() || undefined,
              siteAddress: formData.siteAddress.trim() || undefined,
              siteCity: formData.siteCity ? (typeof formData.siteCity === 'string' ? formData.siteCity : formData.siteCity.toString()) : undefined,
              siteState: formData.siteState ? (typeof formData.siteState === 'string' ? formData.siteState : formData.siteState.toString()) : undefined,
              siteZip: formData.siteZip.trim() || undefined,
              numberOfUnits: formData.numberOfUnits ? (typeof formData.numberOfUnits === 'string' ? parseInt(formData.numberOfUnits) || undefined : formData.numberOfUnits) : undefined,
              physicalAddress: formData.physicalAddress.trim() || undefined,
              city: formData.city ? (typeof formData.city === 'string' ? formData.city : formData.city.toString()) : undefined,
              state: formData.state ? (typeof formData.state === 'string' ? formData.state : formData.state.toString()) : undefined,
              gstNumber: formData.gstNumber.trim() || undefined,
              panNumber: formData.panNumber.trim() || undefined,
              value: formData.budgetRange
                ? Number(formData.budgetRange.replace(/[^0-9]/g, '')) || undefined
                : undefined,
              contactPersonName: formData.contactPersonName.trim() || undefined,
              contactPersonMobile: formData.contactPersonMobile.trim() || undefined,
            };
            await dispatch(createLead(newLead));
          }
        } catch (err) {
          console.error("Failed to create contact:", err);
        }
      }

      onRequestClose();
      return;
    }

    const trimmedReferenceBy = formData.referenceBy.trim();
    const newLead: Partial<Lead> = {
      name: formData.fullName.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim() || undefined,
      whatsapp: formData.whatsapp.trim() || undefined,
      referenceBy: trimmedReferenceBy || undefined,
      company: formData.type === 'business' ? formData.businessName.trim() : formData.fullName.trim(),
      source: formData.leadSource || defaultLeadSource,
      status: !isLeadEntity(initialData) ? 'new' : (initialData.status as any),
      stageId: !isLeadEntity(initialData) ? getStageOrder('new') : (initialData.stageId || getStageOrder('new')),
      detailedStage: !isLeadEntity(initialData) ? 'new' : (initialData.detailedStage || 'new'),
      currentStage: !isLeadEntity(initialData) ? 'new' : (initialData.currentStage || 'new'),
      stage: !isLeadEntity(initialData) ? 'new' : (initialData.stage || 'new'),
      contactId: isContactEntity(existingContact)
        ? existingContact.id
        : (isLeadEntity(existingContact) ? existingContact.contactId || existingContact.contact?.id : (existingContact as any)?.match?.id),
      contactPersonName: formData.contactPersonName.trim() || undefined,
      contactPersonMobile: formData.contactPersonMobile.trim() || undefined,
      siteName: formData.siteName.trim() || undefined,
      siteAddress: formData.siteAddress.trim() || undefined,
      siteCity: formData.siteCity ? (typeof formData.siteCity === 'string' ? formData.siteCity : formData.siteCity.toString()) : undefined,
      siteState: formData.siteState ? (typeof formData.siteState === 'string' ? formData.siteState : formData.siteState.toString()) : undefined,
      siteZip: formData.siteZip.trim() || undefined,
      numberOfUnits: formData.numberOfUnits ? (typeof formData.numberOfUnits === 'string' ? parseInt(formData.numberOfUnits) || undefined : formData.numberOfUnits) : undefined,
      physicalAddress: formData.physicalAddress.trim() || undefined,
      city: formData.city ? (typeof formData.city === 'string' ? formData.city : formData.city.toString()) : undefined,
      state: formData.state ? (typeof formData.state === 'string' ? formData.state : formData.state.toString()) : undefined,
      gstNumber: formData.gstNumber.trim() || undefined,
      panNumber: formData.panNumber.trim() || undefined,
      value: formData.budgetRange
        ? Number(formData.budgetRange.replace(/[^0-9]/g, '')) || undefined
        : undefined,
    };

    if (initialData && !isContactEntity(initialData)) {
      // Editing
      const updatedLead = {
        ...initialData,
        ...newLead,
        // Explicitly clear on edit when user empties the field.
        referenceBy: trimmedReferenceBy || (null as any),
      } as Lead;
      dispatch(updateLead({ id: updatedLead.id, data: updatedLead as any }));
    } else {
      console.log("Create New Lead Payload:", newLead);
      dispatch(createLead(newLead));
    }

    onRequestClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      ></div>

      {/* Slide-in Panel */}
      <div
        className={`absolute right-0 top-0 h-full w-[30%] bg-white shadow-xl transform transition-transform duration-1000 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData && isLeadEntity(initialData) ? 'Edit Lead' : 'Add New Lead'}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              type="button"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Stepper */}
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                className="mb-4"
                connector={<ColorlibConnector />}
              >
                <Step
                  key="Information"
                  onClick={() => handleStepClick(0)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover .MuiStepLabel-root': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <StepLabel
                    StepIconComponent={ColorlibStepIcon}
                    icon="1"
                    sx={{ cursor: 'pointer' }}
                  />
                </Step>
                <Step
                  key="Site"
                  onClick={() => handleStepClick(1)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover .MuiStepLabel-root': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <StepLabel
                    StepIconComponent={ColorlibStepIcon}
                    icon="2"
                    sx={{ cursor: 'pointer' }}
                  />
                </Step>
                <Step
                  key="Review"
                  onClick={() => handleStepClick(2)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover .MuiStepLabel-root': {
                      opacity: 0.8,
                    },
                  }}
                >
                  <StepLabel
                    StepIconComponent={ColorlibStepIcon}
                    icon="3"
                    sx={{ cursor: 'pointer' }}
                  />
                </Step>
              </Stepper>

              {/* Step 1: Information (Contact Information) */}
              {activeStep === 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <InformationCircleIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Contact Information
                    </h3>
                  </div>
                  <div className="mb-4">
                    <DropdownField
                      label="Source"
                      name="leadSource"
                      value={formData.leadSource}
                      onChange={handleChange}
                      options={leadSourceOptions}
                      disabled={isViewMode}
                    />
                  </div>

                  {['Reference', 'Direct'].includes(formData.leadSource) && (
                    <div className="mb-4">
                      <TextInputField
                        label="Refered By"
                        name="referenceBy"
                        value={formData.referenceBy}
                        onChange={handleChange}
                        validationType="name"
                        icon={<UserIcon className="h-5 w-5 text-gray-500" />}
                        disabled={isViewMode}
                        maxLength={20}
                        required={formData.leadSource === 'Reference'}
                      />
                      {errors.referenceBy && (
                        <span className="text-red-500 text-xs block mt-1">{errors.referenceBy}</span>
                      )}
                    </div>
                  )}

                  <div className="mb-4">
                    <Autocomplete
                      freeSolo
                      options={mobileOptions}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                          return option;
                        }
                        return `${option.name} - ${option.phone}`;
                      }}
                      value={
                        // Only set value if there's an existing contact AND the input hasn't been modified
                        // If existingContact is null (cleared when user types), value is null to allow free editing
                        existingContact &&
                          mobileInputValue.includes('-') &&
                          mobileInputValue.split(' - ')[1]?.replace(/[^0-9]/g, '') === formData.phone
                          ? mobileOptions.find(opt => opt.phone === formData.phone && opt.data === existingContact) || null
                          : null
                      }
                      inputValue={mobileInputValue}
                      onInputChange={handleMobileInputChange}
                      onChange={handleMobileChange}
                      disabled={isViewMode}
                      filterOptions={(options, state) => {
                        const input = state.inputValue.toLowerCase();
                        if (!input) {
                          return options;
                        }
                        return options.filter((option) =>
                          (option.name && option.name.toLowerCase().includes(input)) ||
                          (option.phone && option.phone.includes(input)) ||
                          (option.email && option.email.toLowerCase().includes(input))
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Mobile Number"
                          required
                          placeholder="Enter 10-digit mobile number"
                          error={touchedWhileTyping.phone && !!errors.phone}
                          // helperText={touchedWhileTyping.phone && errors.phone ? errors.phone : 'Enter a valid mobile number'}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon className="h-5 w-5 text-gray-500" />
                              </InputAdornment>
                            ),
                          }}
                          inputProps={{
                            ...params.inputProps,
                            maxLength: 10, // Maximum 10 digits for Indian mobile number
                            inputMode: 'numeric',
                            pattern: '[6-9][0-9]{9}',
                            onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => {
                              const target = e.target as HTMLInputElement;
                              const currentValue = target.value || '';
                              const char = String.fromCharCode(e.which || e.keyCode);
                              const isControlKey = e.ctrlKey || e.metaKey || e.altKey ||
                                e.key === 'Backspace' || e.key === 'Delete' ||
                                e.key === 'Tab' || e.key === 'ArrowLeft' ||
                                e.key === 'ArrowRight' || e.key === 'Home' ||
                                e.key === 'End';

                              // Only allow numeric keys (0-9) and control keys
                              if (!/[0-9]/.test(char) && !isControlKey) {
                                e.preventDefault();
                                return;
                              }

                              // If it's a numeric key, validate first digit
                              if (/[0-9]/.test(char)) {
                                // If this is the first digit, it must be 6, 7, 8, or 9
                                if (currentValue.length === 0 && !/^[6-9]$/.test(char)) {
                                  e.preventDefault();
                                  setErrors((prev) => ({
                                    ...prev,
                                    phone: 'Mobile number must start with 6, 7, 8, or 9'
                                  }));
                                  return;
                                }
                                // If already 10 digits, don't allow more
                                if (currentValue.length >= 10) {
                                  e.preventDefault();
                                  return;
                                }
                              }
                            },
                            onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                              // Filter pasted content to only allow valid mobile numbers
                              e.preventDefault();
                              const pastedText = e.clipboardData.getData('text');
                              const numericValue = pastedText.replace(/[^0-9]/g, '').slice(0, 10);

                              // Validate first digit is 6-9
                              if (numericValue && /^[6-9]/.test(numericValue)) {
                                setFormData((prev) => ({
                                  ...prev,
                                  phone: numericValue,
                                }));
                                setMobileInputValue(numericValue);
                                // Clear error if valid
                                if (numericValue.length === 10 && /^[6-9]\d{9}$/.test(numericValue)) {
                                  setErrors((prev) => ({ ...prev, phone: '' }));
                                }
                              } else if (numericValue) {
                                // Invalid first digit
                                setErrors((prev) => ({
                                  ...prev,
                                  phone: 'Enter a valid mobile number'
                                }));
                              }
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{option.name}</span>
                            <span className="text-sm text-gray-500">{option.phone}</span>
                            {/* <span className="text-xs text-gray-400 capitalize">{option.type}</span> */}
                          </div>
                        </li>
                      )}
                      componentsProps={{
                        popper: {
                          style: {
                            zIndex: 20000,
                          },
                        },
                      }}
                    />
                    {errors.phone && !touchedWhileTyping.phone && (
                      <span className="text-red-500 text-xs block mt-1">{errors.phone}</span>
                    )}
                    {/* {existingContact && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              Existing {('status' in existingContact && ['active', 'pending', 'inactive'].includes(existingContact.status as string)) ? 'Contact' : 'Lead'} Selected
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                              {existingContact.name} - {existingContact.email}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleClearExisting}
                            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            Create New
                          </button>
                        </div>
                      </div>
                    )} */}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (!isViewMode) {
                            setFormData((prev) => ({
                              ...prev,
                              type: 'individual',
                              businessName: '',
                              gstNumber: '',
                              panNumber: '',
                            }));
                            setHasGstNumber(false);
                            if (errors.businessName) {
                              setErrors((prev) => ({ ...prev, businessName: '' }));
                            }
                          }
                        }}
                        disabled={isViewMode}
                        className={`
                          flex-1 px-6 py-3.5 text-base font-medium rounded-md border-2 transition-all duration-200
                          ${formData.type === 'individual'
                            ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                          }
                          ${isViewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        Individual
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!isViewMode) {
                            setFormData((prev) => ({
                              ...prev,
                              type: 'business',
                            }));
                          }
                        }}
                        disabled={isViewMode}
                        className={`
                          flex-1 px-6 py-3.5 text-base font-medium rounded-md border-2 transition-all duration-200
                          ${formData.type === 'business'
                            ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                          }
                          ${isViewMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        Business
                      </button>
                    </div>
                  </div>

                  {formData.type === 'business' && (
                    <>
                      <div className="mb-4">
                        <TextInputField
                          label="Business Name"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleChange}
                          required
                          validationType="name"
                          icon={<BuildingOfficeIcon className="h-5 w-5 text-gray-500" />}
                          disabled={isViewMode}
                        />
                        {errors.businessName && (
                          <span className="text-red-500 text-xs block mt-1">{errors.businessName}</span>
                        )}
                      </div>

                      <div className="mb-4">
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            id="hasGstNumber"
                            checked={hasGstNumber}
                            onChange={handleGstCheckboxChange}
                            disabled={isViewMode}
                            className="h-4 w-4"
                          />
                          <label
                            htmlFor="hasGstNumber"
                            className="text-gray-600 text-sm cursor-pointer"
                          >
                            Do you have a GST number?
                          </label>
                        </div>
                      </div>

                      {hasGstNumber ? (
                        <div className="mb-4">
                          <TextInputField
                            label="GST Number"
                            name="gstNumber"
                            value={formData.gstNumber}
                            onChange={handleAlphanumericChange}
                            validationType="none"
                            icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                            disabled={isViewMode}
                            required
                            maxLength={15}
                          />
                          {errors.gstNumber && (
                            <span className="text-red-500 text-xs block mt-1">{errors.gstNumber}</span>
                          )}
                        </div>
                      ) : (
                        <div className="mb-4">
                          <TextInputField
                            label="PAN Number"
                            name="panNumber"
                            value={formData.panNumber}
                            onChange={handleAlphanumericChange}
                            validationType="none"
                            icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                            disabled={isViewMode}
                            required
                            maxLength={10}
                          />
                          {errors.panNumber && (
                            <span className="text-red-500 text-xs block mt-1">{errors.panNumber}</span>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="mb-4">
                    <TextInputField
                      label="Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      validationType="name"
                      icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                      disabled={isViewMode}
                      maxLength={20}
                    />
                    {errors.fullName && (
                      <span className="text-red-500 text-xs block mt-1">{errors.fullName}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <EmailField
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required={false}
                      showError={touchedWhileTyping.email}
                      disabled={isViewMode}
                    />
                  </div>

                  <div className="mb-4">
                    <TextInputField
                      label="Address"
                      name="physicalAddress"
                      value={formData.physicalAddress}
                      onChange={handleChange}
                      validationType="address"
                      icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
                      disabled={isViewMode}
                      maxLength={50}
                    />
                  </div>

                  <div className="mb-4">
                    <CityField
                      label="City"
                      name="city"
                      value={formData.city ?? undefined}
                      state={formData.state ?? undefined}
                      onChange={handleChange}
                      onStateChange={handleStateChange}
                      required
                      disabled={isViewMode}
                    />
                    {errors.city && (
                      <span className="text-red-500 text-xs block mt-1">{errors.city}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <StateField
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      disabled={true}
                    />
                    {errors.state && (
                      <span className="text-red-500 text-xs block mt-1">{errors.state}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        id="sameAsAboveWhatsapp"
                        checked={sameAsAboveWhatsapp}
                        onChange={handleCheckboxChangeWhatsapp}
                        disabled={isViewMode}
                        className="h-4 w-4"
                      />
                      <label
                        htmlFor="sameAsAboveWhatsapp"
                        className="text-gray-600 text-sm cursor-pointer"
                      >
                        Same as mobile number
                      </label>
                    </div>
                  </div>

                  <div className="mb-4">
                    <PhoneField
                      label="WhatsApp"
                      name="whatsapp"
                      value={formData.whatsapp || ''}
                      onChange={handleChange}
                      onBlur={() => handleBlur('whatsapp')}
                      // required
                      showError={touchedWhileTyping.whatsapp}
                      disabled={sameAsAboveWhatsapp || isViewMode}
                    />
                  </div>

                  
                </div>
              )}

              {/* Step 2: Site Information */}
              {activeStep === 1 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Site Information
                    </h3>
                  </div>

                  <div className="mb-4">
                    <TextField
                      label="Number of Units"
                      name="numberOfUnits"
                      value={formData.numberOfUnits}
                      onChange={handleNumberChange}
                      fullWidth
                      required
                      type="text"
                      disabled={isViewMode}
                      error={!!errors.numberOfUnits}
                      helperText={errors.numberOfUnits}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      }}
                    />
                  </div>

                  <div className="mb-4">
                    <TextInputField
                      label="Site Name"
                      name="siteName"
                      value={formData.siteName}
                      onChange={handleChange}
                      required
                      icon={<BuildingOfficeIcon className="h-5 w-5 text-gray-500" />}
                      disabled={isViewMode}
                      maxLength={20}
                    />
                    {errors.siteName && (
                      <span className="text-red-500 text-xs block mt-1">{errors.siteName}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <TextInputField
                      label="Site Address"
                      name="siteAddress"
                      value={formData.siteAddress}
                      onChange={handleChange}
                      required
                      validationType="address"
                      icon={<MapPinIcon className="h-5 w-5 text-gray-500" />}
                      disabled={isViewMode}
                      maxLength={50}
                    />
                    {errors.siteAddress && (
                      <span className="text-red-500 text-xs block mt-1">{errors.siteAddress}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <CityField
                      label="City"
                      name="siteCity"
                      value={formData.siteCity ?? undefined}
                      state={formData.siteState ?? undefined}
                      onChange={handleChange}
                      onStateChange={handleSiteStateChange}
                      required
                    />
                    {errors.siteCity && (
                      <span className="text-red-500 text-xs block mt-1">{errors.siteCity}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <StateField
                      label="State"
                      name="siteState"
                      value={formData.siteState}
                      onChange={handleChange}
                      required
                      disabled={true}
                    />
                    {errors.siteState && (
                      <span className="text-red-500 text-xs block mt-1">{errors.siteState}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <ZipCodeField
                      label="Zip Code"
                      name="siteZip"
                      value={formData.siteZip}
                      onChange={handleChange}
                      onBlur={() => handleBlur('siteZip')}
                      // required
                      showError={touchedWhileTyping.siteZip}
                      disabled={isViewMode}
                    />
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <UserIcon className="h-5 w-5 text-gray-500" />
                      <h4 className="text-md font-semibold text-gray-900">
                        Site Contact Person
                      </h4>
                    </div>
                  </div>

                  <div className="mb-4">
                    <TextInputField
                      label="Name"
                      name="contactPersonName"
                      value={formData.contactPersonName}
                      onChange={handleChange}
                      required
                      validationType="name"
                      icon={<IdentificationIcon className="h-5 w-5 text-gray-500" />}
                      disabled={isViewMode}
                      maxLength={20}
                    />
                    {errors.contactPersonName && (
                      <span className="text-red-500 text-xs block mt-1">{errors.contactPersonName}</span>
                    )}
                  </div>

                  <div className="mb-4">
                    <PhoneField
                      label="Mobile"
                      name="contactPersonMobile"
                      value={formData.contactPersonMobile || ''}
                      onChange={handleChange}
                      onBlur={() => handleBlur('contactPersonMobile')}
                      required
                      showError={touchedWhileTyping.contactPersonMobile}
                      disabled={isViewMode}
                    />
                    {errors.contactPersonMobile && (
                      <span className="text-red-500 text-xs block mt-1">{errors.contactPersonMobile}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3: Summary */}
              {activeStep === 2 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircleIcon className="h-5 w-5 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Review & Save
                    </h3>
                  </div>

                  {/* Contact Information Section */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <InformationCircleIcon className="h-4 w-4" />
                      Contact Information
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <DetailRow label="Type" value={formData.type === 'business' ? 'Business' : 'Individual'} />
                      {formData.type === 'business' && (
                        <>
                          <DetailRow label="Business Name" value={formData.businessName} />
                          {hasGstNumber ? (
                            <DetailRow label="GST Number" value={formData.gstNumber} />
                          ) : (
                            <DetailRow label="PAN Number" value={formData.panNumber} />
                          )}
                        </>
                      )}
                      <DetailRow label="Name" value={formData.fullName} />
                      <DetailRow label="Phone" value={formData.phone} />
                      <DetailRow label="WhatsApp" value={formData.whatsapp} />
                      <DetailRow label="Reference By" value={formData.referenceBy} />
                      <DetailRow label="Address" value={formData.physicalAddress} />
                      <DetailRow
                        label="State"
                        value={formData.state?.toString() || '-'}
                      />
                      <DetailRow
                        label="City"
                        value={formData.city?.toString() || '-'}
                      />
                    </div>
                  </div>

                  {/* Site Information Section */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <BuildingOfficeIcon className="h-4 w-4" />
                      Site Information
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <DetailRow
                        label="Number of Units"
                        value={formData.numberOfUnits}
                      />
                      <DetailRow label="Site Name" value={formData.siteName} />
                      <div className="sm:col-span-2">
                        <DetailRow
                          label="Site Address"
                          value={formData.siteAddress}
                        />
                      </div>
                      <DetailRow
                        label="Site State"
                        value={formData.siteState?.toString() || '-'}
                      />
                      <DetailRow
                        label="Site City"
                        value={formData.siteCity?.toString() || '-'}
                      />
                      <DetailRow label="Zip Code" value={formData.siteZip} />
                      <DetailRow
                        label="Contact Person Name"
                        value={formData.contactPersonName}
                      />
                      <DetailRow
                        label="Contact Person Mobile"
                        value={formData.contactPersonMobile}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Error Messages */}
              {/* {Object.keys(errors).length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                    {Object.values(errors).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )} */}

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-between gap-3 mt-6">
                {activeStep === 0 && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                )}
                {activeStep === 0 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  >
                    Next
                  </button>
                )}
                {activeStep > 0 && activeStep < 2 && (
                  <>
                    <button
                      type="button"
                      onClick={handleBack}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleNext}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    >
                      Next
                    </button>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    <button
                      type="button"
                      onClick={handleEditClick}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Edit
                    </button>
                    <button
                      type="submit"
                      disabled={!isFormValid() || isViewMode}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmText="Yes, Close"
        cancelText="Cancel"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </div>
  );
};

export default AddLeadModal;


